import React, { useEffect, useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Tabs from "../../../../components/Tabs/Tabs.tsx";
import { useTheme } from "../../../../contexts/Theme/ThemeContext.tsx";
import { TipoVariacao, NivelVariacao, VariacaoV2 } from "../../../../types/Produto.ts";
import { VariationLevelInput } from "./VariationLevelInput/VariationLevelInput.tsx";
import { GradeVariacoes } from "./GradeVariacoes/GradeVariacoes.tsx";
import { ModalTiposVariacao } from "../../../../components/ModalTiposVariacao/ModalTiposVariacao.tsx";
import { MockupProduto } from "../MockupProduto/MockupProduto.tsx";
import { VariacoesBoxV2Container } from "./styles.ts";
import { ReactComponent as Plus } from "../../../../assets/newImages/icons/plus.svg";
import LockedIcon from "../../../../components/LockedIcon/LockedIcon.tsx";
import { CategoriaBox } from "../cadastrosBox/Categorias/Categorias.tsx";

type ActiveTabType = "variacoes" | "preview" | "categorias";

interface VariacoesBoxV2Props {
  niveisVariacao: NivelVariacao[];
  variacoes: VariacaoV2[];
  tiposVariacao: TipoVariacao[];
  listasDePreco: any[];
  temEstoque: boolean;
  permitir_edicao: boolean;
  permitir_visual?: boolean;
  tabelasBloqueadas: boolean;
  onSetNivelTipo: (ordem: number, tipo: TipoVariacao) => void;
  onSetNivelValores: (ordem: number, valores: string[]) => void;
  onRemoveNivel: (ordem: number) => void;
  onSwapNiveis?: () => void;
  onUpdateVariacao: (index: number, field: string, value: any) => void;
  onDeleteVariacao: (index: number) => void;
  onRestaurar: () => void;
  onNovaListaDePreco: () => void;
  onReplicar: (field: string, value: any, precoIndex?: number) => void;
  onTiposUpdate: (tipos: TipoVariacao[]) => void;
  produto: {
    nome: string;
    referencia: string;
    descricao: string;
    fotos: any[];
  };
  temDestaque: boolean;
  tagDestaque: string;
  sugestoesVariacao?: Record<string, string[]>;
  secundariaBloqueada?: boolean;
  temVariacao: boolean;
  forceTab?: { tab: string; ts: number } | null;
  closeCategoriasSignal?: number;
  onCloseCategorias?: () => void;
  config_integracao: {
    permitir_produtos: boolean;
    permitir_imagens: boolean;
    permitir_observacoes: boolean;
  };
  onCategoriasChange: () => void;
  permissoesPlano: any;
}

// Sortable wrapper for a level
const SortableLevelItem: React.FC<{
  id: string;
  children: (dragHandleProps: any) => React.ReactNode;
}> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
};

export const VariacoesBoxV2: React.FC<VariacoesBoxV2Props> = ({
  niveisVariacao,
  variacoes,
  tiposVariacao,
  listasDePreco,
  temEstoque,
  permitir_edicao,
  permitir_visual,
  tabelasBloqueadas,
  onSetNivelTipo,
  onSetNivelValores,
  onRemoveNivel,
  onSwapNiveis,
  onUpdateVariacao,
  onDeleteVariacao,
  onRestaurar,
  onNovaListaDePreco,
  onReplicar,
  onTiposUpdate,
  produto,
  temDestaque,
  tagDestaque,
  sugestoesVariacao = {},
  secundariaBloqueada = false,
  temVariacao,
  forceTab,
  closeCategoriasSignal,
  onCloseCategorias,
  config_integracao,
  onCategoriasChange,
  permissoesPlano,
}) => {
  const { theme } = useTheme();
  const [tabs, setTabs] = useState(
    temVariacao
      ? [
          { name: "Variações", value: "variacoes" },
          { name: "Visualizar produto", value: "preview" },
        ]
      : []
  );
  const [activeTab, setActiveTab] = useState<ActiveTabType>(temVariacao ? "variacoes" : "categorias");
  const [modalTiposOpen, setModalTiposOpen] = useState(false);
  const [showSecondLevel, setShowSecondLevel] = useState(niveisVariacao.length > 1);

  useEffect(() => {
    setShowSecondLevel(niveisVariacao.length > 1);
  }, [niveisVariacao.length]);

  // forceTab: permite que Produto.tsx force a abertura de uma aba específica
  useEffect(() => {
    if (forceTab && forceTab.tab === "categorias") {
      setTabs((prev) => {
        if (prev.some((t) => t.value === "categorias")) return prev;
        return [...prev, { name: "Categorias", value: "categorias" }];
      });
      setActiveTab("categorias");
    }
  }, [forceTab]);

  // Fecha a aba Categorias quando o pai sinaliza (ex.: usuário selecionou uma
  // categoria no select). Só age se a aba estiver ativa no momento.
  useEffect(() => {
    if (!closeCategoriasSignal) return;
    if (activeTab !== "categorias") return;
    if (temVariacao) {
      setActiveTab("variacoes");
      setTabs((prev) => prev.filter((t) => t.value !== "categorias"));
    } else {
      onCloseCategorias?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeCategoriasSignal]);

  // Sincroniza as abas e a aba ativa quando temVariacao muda
  useEffect(() => {
    if (temVariacao) {
      setTabs((prev) => {
        const hasCategorias = prev.some((t) => t.value === "categorias");
        const base = [
          { name: "Variações", value: "variacoes" },
          { name: "Visualizar produto", value: "preview" },
        ];
        return hasCategorias
          ? [...base, { name: "Categorias", value: "categorias" }]
          : base;
      });
      setActiveTab("variacoes");
    } else {
      setTabs((prev) => prev.filter((t) => t.value === "categorias"));
      setActiveTab("categorias");
    }
  }, [temVariacao]);

  const nivel0 = niveisVariacao.find((n) => n.ordem === 0) || null;
  const nivel1 = niveisVariacao.find((n) => n.ordem === 1) || null;

  const helperTextExemplos: Record<number, string> = {
    1: "P, M, G.",          // Tamanho
    2: "Ouro, Prata, Rosé.", // Cor/banho
    3: "Feminino, Masculino.", // Gênero
  };

  const getHelperText = (nivel: typeof nivel0) => {
    const exemplo = nivel ? helperTextExemplos[nivel.tipo.id] : null;
    return (
      <>
        {`Informe os valores separados por vírgula ou enter.${exemplo ? ` Exemplo: ${exemplo}` : ''} `}
        <strong>Clique na variação para adicionar uma foto ou cor a ela.</strong>
      </>
    );
  };

  // Filter available types - exclude those already used and inactive
  const tiposUsados = niveisVariacao.map((n) => n.tipo.id);
  const tiposAtivos = tiposVariacao.filter((t) => t.id !== 4);
  const tiposDisponiveisNivel0 = tiposAtivos.filter(
    (t) => !tiposUsados.includes(t.id) || (nivel0 && nivel0.tipo.id === t.id)
  );
  const tiposDisponiveisNivel1 = tiposAtivos.filter(
    (t) => !tiposUsados.includes(t.id) || (nivel1 && nivel1.tipo.id === t.id)
  );

  const hasVariacoes = variacoes.filter((v) => Number(v.ativo) === 1).length > 0;

  const sugestoesNivel0 = useMemo(() => {
    if (!nivel0) return [];
    // Suporte a chave por nome (novo) ou por ID (legado)
    return sugestoesVariacao[nivel0.tipo.nome] || sugestoesVariacao[nivel0.tipo.id.toString()] || [];
  }, [nivel0, sugestoesVariacao]);

  const sugestoesNivel1 = useMemo(() => {
    if (!nivel1) return [];
    return sugestoesVariacao[nivel1.tipo.nome] || sugestoesVariacao[nivel1.tipo.id.toString()] || [];
  }, [nivel1, sugestoesVariacao]);

  // Drag & drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onSwapNiveis) {
      onSwapNiveis();
    }
  };

  const canDrag = nivel0 && nivel1 && permitir_edicao;

  const renderLevels = () => {
    const nivel0Element = (dragHandleProps?: any) => (
      <VariationLevelInput
        nivel={nivel0}
        label="Variação principal"
        tiposDisponiveis={tiposDisponiveisNivel0}
        todosOsTipos={tiposAtivos}
        onSelectTipo={(tipo) => onSetNivelTipo(0, tipo)}
        onValoresChange={(valores) => onSetNivelValores(0, valores)}
        onRemove={() => onRemoveNivel(0)}
        onOpenTiposModal={() => setModalTiposOpen(true)}
        permitir_edicao={permitir_edicao}
        permitir_visual={permitir_visual}
        dragHandleProps={dragHandleProps}
        helperText={getHelperText(nivel0)}
        suggestions={sugestoesNivel0}
      />
    );

    const nivel1Element = (dragHandleProps?: any) => (
      <VariationLevelInput
        nivel={nivel1}
        label="Variação secundária"
        tiposDisponiveis={tiposDisponiveisNivel1}
        todosOsTipos={tiposAtivos}
        onSelectTipo={(tipo) => onSetNivelTipo(1, tipo)}
        onValoresChange={(valores) => onSetNivelValores(1, valores)}
        onRemove={() => {
          onRemoveNivel(1);
          setShowSecondLevel(false);
        }}
        onOpenTiposModal={() => setModalTiposOpen(true)}
        permitir_edicao={permitir_edicao}
        permitir_visual={permitir_visual}
        dragHandleProps={dragHandleProps}
        helperText={getHelperText(nivel1)}
        suggestions={sugestoesNivel1}
      />
    );

    if (canDrag) {
      const items = ["nivel-0", "nivel-1"];
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <SortableLevelItem id="nivel-0">
              {(dragHandleProps) => nivel0Element(dragHandleProps)}
            </SortableLevelItem>
            <SortableLevelItem id="nivel-1">
              {(dragHandleProps) => nivel1Element(dragHandleProps)}
            </SortableLevelItem>
          </SortableContext>
        </DndContext>
      );
    }

    return (
      <>
        {nivel0Element()}
        {nivel0 && (showSecondLevel || nivel1) ? (
          nivel1Element()
        ) : (
          nivel0 && permitir_edicao && (
            <div className="add-nivel">
              <div
                className={`adicionar-secundaria fonteTexto${secundariaBloqueada ? ' bloqueado' : ''}`}
                onClick={() => !secundariaBloqueada && setShowSecondLevel(true)}
              >
                <Plus className="plus" /> Adicionar variação secundária
              </div>
              {secundariaBloqueada && (
                <LockedIcon
                  mensagem="Recurso disponível a partir do plano Essencial."
                  width="2.6rem"
                  className="blocked"
                />
              )}
            </div>
          )
        )}
      </>
    );
  };

  return (
    <VariacoesBoxV2Container theme={theme}>
      {tabs.length > 0 && (
        <Tabs
          tabs={tabs}
          tabsNumber={tabs.length}
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value as ActiveTabType);
            if (value !== "categorias") {
              setTabs((prev) => prev.filter((t) => t.value !== "categorias"));
            }
          }}
        />
      )}

      {temVariacao && (
        <div style={{ display: activeTab === "variacoes" ? "block" : "none" }}>
          <div className="texto fonteTexto">
            Utilize os tipos de variação para diferenciar seu produto pelas
            características dele. Selecione o tipo e informe os valores.
          </div>

          <div className="niveis-container">
            {renderLevels()}
          </div>

          {/* Grade */}
          {hasVariacoes && (
            <GradeVariacoes
              variacoes={variacoes}
              listasDePreco={listasDePreco}
              temEstoque={temEstoque}
              permitir_edicao={permitir_edicao}
              tabelasBloqueadas={tabelasBloqueadas}
              onUpdateVariacao={onUpdateVariacao}
              onDeleteVariacao={onDeleteVariacao}
              onRestaurar={onRestaurar}
              onNovaListaDePreco={onNovaListaDePreco}
              onReplicar={onReplicar}
            />
          )}
        </div>
      )}

      {temVariacao && (
        <div style={{ display: activeTab === "preview" ? "block" : "none" }}>
          <div className="texto fonteTexto">
            Confira uma prévia de como seu produto irá aparecer no catálogo.
          </div>
          <MockupProduto
            produto={produto}
            variacoes={variacoes}
            niveisVariacao={niveisVariacao}
            listasDePreco={listasDePreco}
            temEstoque={temEstoque}
            temDestaque={temDestaque}
            tagDestaque={tagDestaque}
          />
        </div>
      )}

      <div style={{ display: activeTab === "categorias" ? "block" : "none" }}>
        <CategoriaBox
          config_integracao={config_integracao}
          onCategoriasChange={onCategoriasChange}
          permissoesPlano={permissoesPlano}
        />
      </div>

      {/* Modal de tipos */}
      <ModalTiposVariacao
        isOpen={modalTiposOpen}
        setModal={setModalTiposOpen}
        onSave={onTiposUpdate}
      />
    </VariacoesBoxV2Container>
  );
};
